import type { RoundCommandEvent } from "../round/round-reducer.js";
import { RoundCommandError } from "../round/round-errors.js";
import {
  isRoundV3,
  type RoundSnapshot,
  type RoundSnapshotV3,
} from "../round/round-types.js";
import type { ContestSnapshot } from "./contest-types.js";
import {
  selectAvailableRemainderHoles,
  selectImportedRemainderHoles,
  selectContests,
  selectNextContestHole,
} from "./contest-selectors.js";
import { freezeFinishedMatches, replaceContest } from "./contest-state.js";

export function applyContestCommand(
  snapshot: RoundSnapshot,
  command: RoundCommandEvent,
): RoundSnapshotV3 {
  if (!isRoundV3(snapshot) || snapshot.state !== "active")
    throw new RoundCommandError("Contest changes require an active v3 round.");
  if (!Number.isFinite(Date.parse(command.occurredAt)))
    throw new RoundCommandError("The contest agreement time is invalid.");
  if (command.type === "contest.remainder.agreed")
    return agreeRemainder(snapshot, command);
  return recordConcession(snapshot, command);
}

function recordConcession(
  snapshot: RoundSnapshotV3,
  command: RoundCommandEvent,
): RoundSnapshotV3 {
  const payload = command.payload;
  if (!("contestId" in payload) || !("concedingSideId" in payload))
    throw new RoundCommandError("Concession details are incomplete.");
  const contest = requireContest(snapshot, payload.contestId);
  if (contest.format !== "match-play" || !contest.lockedAt || contest.result)
    throw new RoundCommandError(
      "Only an undecided, agreed Match Play contest can be conceded.",
    );
  if (!contest.sides.some((side) => side.id === payload.concedingSideId))
    throw new RoundCommandError("The conceding side is not in this contest.");
  const concession = {
    concedingSideId: payload.concedingSideId,
    recordedAt: command.occurredAt,
    eventId: command.eventId,
  };
  let updated: ContestSnapshot;
  if (command.type === "contest.hole.conceded") {
    if (
      !("holeNumber" in payload) ||
      payload.holeNumber !== selectNextContestHole(snapshot, contest.id)
    )
      throw new RoundCommandError(
        "Only the next unresolved match hole can be conceded.",
      );
    updated = {
      ...contest,
      holeConcessions: {
        ...contest.holeConcessions,
        [payload.holeNumber]: concession,
      },
    };
  } else if (command.type === "contest.match.conceded")
    updated = { ...contest, matchConcession: concession };
  else throw new RoundCommandError("The contest command is not supported.");
  return freezeFinishedMatches({
    ...replaceContest(snapshot, updated),
    version: snapshot.version + 1,
  });
}

function agreeRemainder(
  snapshot: RoundSnapshotV3,
  command: RoundCommandEvent,
): RoundSnapshotV3 {
  const payload = command.payload;
  if (
    !("parentContestId" in payload) ||
    !("holeNumbers" in payload) ||
    !("id" in payload)
  )
    throw new RoundCommandError("The next contest agreement is incomplete.");
  const contests = selectContests(snapshot);
  const parent = requireContest(snapshot, payload.parentContestId);
  if (
    parent.id !== contests.at(-1)!.id ||
    parent.format !== "match-play" ||
    !parent.result
  )
    throw new RoundCommandError(
      "Finish the current Match Play contest before agreeing the next point.",
    );
  if (
    !payload.id.trim() ||
    contests.some((contest) => contest.id === payload.id)
  )
    throw new RoundCommandError("The next contest needs a unique identity.");
  const imported =
    "importedAgreement" in payload && payload.importedAgreement === true;
  const available = imported
    ? selectImportedRemainderHoles(snapshot, parent.id)
    : selectAvailableRemainderHoles(snapshot, parent.id);
  if (
    !available.length ||
    available.length !== payload.holeNumbers.length ||
    available.some((number, index) => number !== payload.holeNumbers[index])
  )
    throw new RoundCommandError(
      "Agree the next point on the still-unplayed remaining holes before recording any of them.",
    );
  const next: ContestSnapshot = {
    id: payload.id,
    parentContestId: parent.id,
    scope: "rivalry_only",
    format: "match-play",
    scoringBasis: parent.scoringBasis,
    sides: structuredClone(parent.sides),
    holeNumbers: [...available],
    rulesVersion: 1,
    lockedAt: command.occurredAt,
    ...(imported ? { agreementSource: "import-review" as const } : {}),
    result: null,
  };
  const updated = {
    ...snapshot,
    version: snapshot.version + 1,
    remainderContests: [...(snapshot.remainderContests ?? []), next],
  };
  return imported ? freezeFinishedMatches(updated) : updated;
}

function requireContest(
  snapshot: RoundSnapshotV3,
  id: string,
): ContestSnapshot {
  const contest = selectContests(snapshot).find((item) => item.id === id);
  if (!contest)
    throw new RoundCommandError("The contest does not belong to this round.");
  return contest;
}
